export type WebStorageName = 'localStorage' | 'sessionStorage';

export type SafeWebStorage = {
  getItem: (key: string) => string | null;
  setItem: (key: string, value: string) => void;
  removeItem: (key: string) => void;
  clear: () => void;
};

const memoryByName: Record<WebStorageName, Map<string, string>> = {
  localStorage: new Map(),
  sessionStorage: new Map(),
};

const nativeCache: Partial<Record<WebStorageName, Storage | null>> = {};

const createMemoryStorage = (name: WebStorageName): SafeWebStorage => {
  const memory = memoryByName[name];

  return {
    getItem: key => (memory.has(key) ? memory.get(key) ?? null : null),
    setItem: (key, value) => {
      memory.set(key, value);
    },
    removeItem: key => {
      memory.delete(key);
    },
    clear: () => {
      memory.clear();
    },
  };
};

const getNativeStorage = (name: WebStorageName): Storage | null => {
  if (name in nativeCache) {
    return nativeCache[name] ?? null;
  }

  if (typeof window === 'undefined') {
    nativeCache[name] = null;
    return null;
  }

  try {
    const storage = window[name];
    const probeKey = '__mm_storage_probe__';
    storage.setItem(probeKey, '1');
    storage.removeItem(probeKey);
    nativeCache[name] = storage;
    return storage;
  } catch {
    nativeCache[name] = null;
    return null;
  }
};

export const resetSafeWebStorageForTests = (): void => {
  memoryByName.localStorage.clear();
  memoryByName.sessionStorage.clear();
  delete nativeCache.localStorage;
  delete nativeCache.sessionStorage;
};

const getSafeWebStorage = (name: WebStorageName): SafeWebStorage => {
  const native = getNativeStorage(name);
  if (!native) {
    return createMemoryStorage(name);
  }

  const memory = createMemoryStorage(name);

  return {
    getItem: key => {
      try {
        const value = native.getItem(key);
        if (value !== null) {
          return value;
        }
      } catch {
        // Native read failed; use in-memory fallback.
      }
      return memory.getItem(key);
    },
    setItem: (key, value) => {
      try {
        native.setItem(key, value);
      } catch {
        memory.setItem(key, value);
      }
    },
    removeItem: key => {
      try {
        native.removeItem(key);
      } catch {
        // Still drop the in-memory copy.
      }
      memory.removeItem(key);
    },
    clear: () => {
      try {
        native.clear();
      } catch {
        // Still drop the in-memory copy.
      }
      memory.clear();
    },
  };
};

export default getSafeWebStorage;
